import type { FrameName } from "@/game/entities/config";
import type { quests } from "@/game/entities/config/quests/quests";
import type { UpgradeIds } from "@/game/entities/config/upgrades";
import type { OfferType, PromotionContext } from "@/types/ui";
import type {
	AllowedSkin,
	EventPassSkin,
	ProvisionSkin,
} from "@/utils/shop/skins";
import { ResourceType } from "../game/entities/config/resources";

export enum OfferStatus {
	NOT_ENOUGH_RESOURCES = "NOT_ENOUGH_RESOURCES",
	HAS_ENOUGH_RESOURCES = "HAS_ENOUGH_RESOURCES",
	SUCCESS = "SUCCESS",
	FAILED = "FAILED",
}

export interface ShopItem {
	store_id: string;
	is_active: boolean;
	currency: string;
	name: string;
	start_date: string | Date;
	end_date: string | Date | null;
	attributes: ShopItemAttributes;
	retail_price: number;
	show_in_left_box: boolean;
	num_of_purchases: number;
}

export enum OfferTemplate {
	SLOTS_1 = "slots_1",
	SLOTS_2 = "slots_2",
	SLOTS_3 = "slots_3",
	SLOTS_4 = "slots_4",
	PIGGY_BANK = "piggy_bank",
	BUNDLE = "bundle",
	EVENT_PASS = "event_pass",
	PROVISION = "provision",
}

export enum CommodityGroup {
	BUILDING = "building",
	UPGRADE = "upgrade",
	RESOURCE = "resource",
	UTILITY = "utility",
	EVENT_RESOURCE = "event_resource",
}

export type ResourceItem =
	| ResourceType.COINS
	| ResourceType.GEMS
	| ResourceType.LUMBER
	| ResourceType.STONE
	| ResourceType.CLAY
	| ResourceType.IRON
	| ResourceType.GOODS
	| ResourceType.STEEL
	| ResourceType.ARMY
	| ResourceType.COINS_MULTIPLIER;

export type UtilityItem = ResourceType.SUPER_CLICKER | ResourceType.AD_SKIPPER;

// NOTE: phantom event resource for FE
export enum EventAdditionalResource {
	CHEST_KEY = ResourceType.CHEST_KEY,
}

export enum EventConversionOffersRate {
	RATE_5 = "event_conversion_offers_rate_5",
	RATE_10 = "event_conversion_offers_rate_10",
	RATE_15 = "event_conversion_offers_rate_15",
	RATE_20 = "event_conversion_offers_rate_20",
	RATE_30 = "event_conversion_offers_rate_30",
}

export enum EventUpgrades {
	CHRISTMAS_DREAM = "ch_dream_1",
	CHRISTMAS_DREAM_3 = "ch_dream_3",
	EASTER_EVENTIUM_INCOME = "ea_eventium_income",
	EASTER_EVENTIUM_INCOME_2 = "ea_eventium_income2",
	EASTER_EVENTIUM_INCOME_3 = "ea_eventium_income3",
	EASTER_EVENTIUM_INCOME_4 = "ea_eventium_income4",
	EASTER_EVENTIUM_INCOME_5 = "ea_eventium_income5",
	EASTER_TIDE_SPECIAL_1 = "ea_eastertide_special1",
	EASTER_TIDE_SPECIAL_2 = "ea_eastertide_special2",
	EASTER_TIDE_SPECIAL_4 = "ea_eastertide_special4",
	FISHING_EVENTIUM_INCOME = "fi_eventium_income",
	FISHING_EVENTIUM_INCOME_2 = "fi_eventium_income2",
	FIREFEST_SPECIAL_1 = "ff_special_01",
	FIREFEST_SPECIAL_2 = "ff_special_02",
	FIREFEST_SPECIAL_3 = "ff_special_03",
	FIREFEST_SPECIAL_4 = "ff_special_04",
	FIREFEST_EVENTIUM_INCOME = "ff_bundle_1",
	FIREFEST_EVENTIUM_INCOME_2 = "ff_bundle_2",
	FIREFEST_EVENTIUM_INCOME_3 = "ff_bundle_3",
	FIREFEST_EVENTIUM_INCOME_4 = "ff_bundle_4",
	SOLARFAIR_SPECIAL_1 = "sf_special_01",
	SOLARFAIR_SPECIAL_2 = "sf_special_02",
	SOLARFAIR_SPECIAL_3 = "sf_special_03",
	SOLARFAIR_EVENTIUM_INCOME = "sf_bundle_1",
	SOLARFAIR_EVENTIUM_INCOME_2 = "sf_bundle_2",
	SOLARFAIR_EVENTIUM_INCOME_3 = "sf_bundle_3",
	HUNTING_EVENTIUM_INCOME = "hu_bow_1",
	HUNTING_EVENTIUM_INCOME_2 = "hu_bow_3",
	HUNTING_EVENTIUM_INCOME_3 = "hu_horse_2",
	HUNTING_EVENTIUM_INCOME_4 = "hu_horse_4",
	ALL_HALLOWS_SPECIAL_1 = "ha_special1",
	ALL_HALLOWS_SPECIAL_2 = "ha_special2",
	ALL_HALLOWS_SPECIAL_4 = "ha_special4",
	HALLOWEEN_EVENTIUM_INCOME = "ha_fire1",
	HALLOWEEN_EVENTIUM_INCOME_2 = "ha_fire2",
	HALLOWEEN_EVENTIUM_INCOME_3 = "ha_fire3",
	HALLOWEEN_EVENTIUM_INCOME_4 = "ha_fire4",
	PIRATES_HUNTERS_PACK_1 = "pi_pack1",
	PIRATES_HUNTERS_PACK_2 = "pi_pack2",
	PIRATES_HUNTERS_PACK_4 = "pi_pack4",
	PIRATES_EVENTIUM_INCOME_1 = "pi_bundle1",
	PIRATES_EVENTIUM_INCOME_2 = "pi_bundle2",
	PIRATES_EVENTIUM_INCOME_3 = "pi_bundle3",
	PIRATES_EVENTIUM_INCOME_4 = "pi_bundle4",
}

export type EventResourceItem =
	| ResourceType.EVENTIUM
	| EventUpgrades
	| EventConversionOffersRate
	| EventAdditionalResource;

export type SlotItem =
	| { group: CommodityGroup.BUILDING; frame_name: FrameName; qty: number }
	| { group: CommodityGroup.UPGRADE; upgrade_id: UpgradeIds; qty: number }
	| { group: CommodityGroup.RESOURCE; key: ResourceItem; qty: number }
	| { group: CommodityGroup.UTILITY; key: UtilityItem; qty: number }
	| {
			group: CommodityGroup.EVENT_RESOURCE;
			key: EventResourceItem;
			qty: number;
	  };

export type ResourceOrUtilitySlotItem = Extract<
	SlotItem,
	{ group: CommodityGroup.RESOURCE | CommodityGroup.UTILITY }
>;

export type EventResourceSlotItem = Extract<
	SlotItem,
	{ group: CommodityGroup.EVENT_RESOURCE }
>;

export type BuildingSlotItem = Extract<
	SlotItem,
	{ group: CommodityGroup.BUILDING }
>;

export type UpgradeSlotItem = Extract<
	SlotItem,
	{ group: CommodityGroup.UPGRADE }
>;

export type QuestId =
	ReturnType<typeof quests.keys> extends IterableIterator<infer K>
		? K
		: number;

export type Slot = { items: Array<SlotItem>; text?: string };

export type OfferSlots =
	// fixed-length tuple
	| { template: OfferTemplate.SLOTS_1; slots: [Slot] }
	| { template: OfferTemplate.SLOTS_2; slots: [Slot, Slot] }
	| { template: OfferTemplate.SLOTS_3; slots: [Slot, Slot, Slot] }
	| { template: OfferTemplate.SLOTS_4; slots: [Slot, Slot, Slot, Slot] }
	| { template: OfferTemplate.PIGGY_BANK; slots: [Slot] }
	| { template: OfferTemplate.BUNDLE; slots: Array<Slot> }
	| { template: OfferTemplate.EVENT_PASS; slots?: Array<never> }
	| { template: OfferTemplate.PROVISION; slots?: Array<never> };

export type BundleRole = "ITEM" | "ALL_IN_ONE";

export enum OfferCategory {
	EVENT = "event",
	SPECIAL = "special",
	REGULAR = "regular",
}

export enum OfferSubtype {
	WelcomeBackReward = "welcome_back_reward",
	WelcomeBackOffer = "welcome_back_offer",
}

export enum OfferFlagLabels {
	MOST_POPULAR = "most popular",
	BEST_VALUE = "best value",
}

export enum OfferComplementaryReward {
	EVENT_PASS = "event_pass",
	PROVISION = "provision",
}

export type SkinRule = {
	enums: typeof EventPassSkin | typeof ProvisionSkin;
	reward: OfferComplementaryReward;
};

export type ShopItemAttributesBase = {
	subtype?: OfferSubtype;
	level_unlock?: number;
	limit?: number;
	popup_delay_seconds?: number;
	high_priority?: boolean;
	skin?: AllowedSkin; // all in one = template images, colors, & LB, cluster position
	multiplier_value?: number;
	promo_text?: string;
	promo_bonus_percent?: 50 | 70;
	promotion_context?: PromotionContext;
	percentage_discount?: number;
	percentage_value?: number;
	activity?: QuestId; // Piggy Bank
	segmentation?: SegmentationValue;
	prerequisite_store_id?: string;
	category?: OfferCategory;
	flag?: OfferFlagLabels;
	hide_remaining_time?: boolean;
	reward_duration_days?: number;
	reward_duration_days_origin?: number;
	black_friday_stamp?: boolean;
};

export type SegmentationValue = {
	minSpendShop?: number;
	maxSpendShop?: number;
	minSpendShopLeftBox?: number;
	maxSpendShopLeftBox?: number;
	minLevelShop?: number;
	maxLevelShop?: number;
	minLevelShopLeftBox?: number;
	maxLevelShopLeftBox?: number;
	daysSinceLastPurchase?: number;
	daysSinceRegistration?: number;
};

export type ShopItemAttributes = ShopItemAttributesBase & OfferSlots;

export type RuleError = { path: string; code: string; message: string };

// event conversion offer
export interface ConversionOffer {
	id: number;
	value: Partial<Record<ResourceType, number | (() => number)>> | null;
	price: Partial<Record<ResourceType, number>> | null;
	header?: string;
	description?: string;
	replaceValue?: string | number;
	timeLimit?: number | null; // seconds
	ribbonInfo?: string | null;
	isBought?: boolean;
	limit?: {
		current: number;
		max: number;
	} | null;
	type: OfferType;
	iconName?: string;
	callback?: () => void;
	condition?: (() => boolean) | null;
	conditionForNotAvailability?: (() => boolean) | null;
	dynamicDescription?: (() => string) | null;
}
